/**
 * The single Clerk `appearance` object used by every Clerk-rendered surface.
 *
 * Everything here goes through Clerk's supported appearance API — `variables`
 * for the palette, `elements` for per-descriptor overrides — rather than global
 * CSS aimed at Clerk's internal class names, which change between releases.
 * Written against @clerk/react 6.x.
 *
 * Two deliberate decisions:
 *
 *  - Clerk's card chrome is switched off (`cardBox`/`card`) because OMJ VISION
 *    draws the card itself in Auth.css. Clerk keeping its own card inside ours
 *    is what produced the doubled borders and uneven padding.
 *  - `footerAction` is hidden. In `<SignIn>` that slot holds the "Don't have an
 *    account? Sign up" call to action, and OMJ VISION is invite-only. The
 *    surrounding `footer` stays visible, so Clerk's own "Secured by Clerk"
 *    branding is untouched.
 */

const GOLD = "#e2ae45";
const GOLD_HOVER = "#f0bd55";
const GOLD_RING = "rgba(226, 174, 69, 0.24)";
const FIELD_BG = "#0f1114";
const BORDER = "#33373d";
const BORDER_HOVER = "#565c66";

export const clerkAppearance = {
  variables: {
    colorPrimary: GOLD,
    colorBackground: "#1b1d21",
    colorText: "#f3f4f5",
    // Clerk's default secondary grey is too dim against near-black; this is
    // the same family but clears 4.5:1 on our card.
    colorTextSecondary: "#b3b9c2",
    colorTextOnPrimaryBackground: "#17120a",
    colorInputBackground: FIELD_BG,
    colorInputText: "#ffffff",
    colorDanger: "#ff8f8f",
    colorSuccess: "#7fe0a0",
    colorWarning: "#f0bd55",
    borderRadius: "10px",
    fontSize: "0.95rem",
  },

  elements: {
    rootBox: { width: "100%" },

    // OMJ VISION owns the card; Clerk's is flattened onto it.
    cardBox: {
      width: "100%",
      border: "none",
      boxShadow: "none",
      background: "transparent",
    },
    card: {
      width: "100%",
      border: "none",
      boxShadow: "none",
      background: "transparent",
      padding: 0,
      gap: "20px",
    },

    // The kicker in Auth.css already brands the card.
    logoBox: { display: "none" },

    header: { gap: "6px", textAlign: "center" },
    headerTitle: {
      fontSize: "1.3rem",
      fontWeight: 700,
      color: "#ffffff",
      letterSpacing: "-0.01em",
    },
    // Carries Clerk's step instructions, including "Enter the verification
    // code sent to …". It has to stay readable.
    headerSubtitle: {
      fontSize: "0.9rem",
      lineHeight: 1.55,
      color: "#b3b9c2",
    },

    main: { gap: "18px" },
    form: { gap: "16px" },
    formFieldLabel: {
      fontSize: "0.82rem",
      fontWeight: 600,
      color: "#c8cdd4",
    },
    formFieldInput: {
      height: "44px",
      fontSize: "0.95rem",
      color: "#ffffff",
      background: FIELD_BG,
      border: `1px solid ${BORDER}`,
      boxShadow: "none",
      "&:hover": { borderColor: BORDER_HOVER },
      "&:focus": {
        borderColor: GOLD,
        boxShadow: `0 0 0 3px ${GOLD_RING}`,
      },
    },
    formFieldAction: { color: GOLD, fontWeight: 600 },

    formButtonPrimary: {
      height: "44px",
      fontSize: "0.95rem",
      fontWeight: 700,
      textTransform: "none",
      letterSpacing: "0.01em",
      background: GOLD,
      color: "#17120a",
      border: "none",
      boxShadow: "none",
      "&:hover": { background: GOLD_HOVER },
      "&:active": { background: "#d3a03c" },
      "&:focus": { boxShadow: `0 0 0 3px ${GOLD_RING}` },
    },

    // Verification code. Clerk renders one input per digit; these are sized
    // and coloured so every digit reads clearly on the dark ground.
    // Six of these sit on one row. At the old `clamp(36px, 12vw, …)` floor plus
    // an 8px gap the row measured ~270px, which is wider than the auth card's
    // content box on a 320px phone. These numbers fit 320px with room over.
    otpCodeFieldInputs: { gap: "6px" },
    otpCodeFieldInput: {
      width: "clamp(30px, 11vw, 46px)",
      height: "52px",
      fontSize: "1.35rem",
      fontWeight: 700,
      color: "#ffffff",
      background: FIELD_BG,
      border: `1px solid #3a3f47`,
      borderRadius: "10px",
      caretColor: GOLD,
      boxShadow: "none",
      "&:hover": { borderColor: BORDER_HOVER },
      "&:focus": {
        borderColor: GOLD,
        boxShadow: `0 0 0 3px ${GOLD_RING}`,
      },
    },
    formResendCodeLink: { color: GOLD, fontWeight: 600 },

    // Shows which address the code went to.
    identityPreview: {
      background: FIELD_BG,
      border: `1px solid #2b2f35`,
    },
    identityPreviewText: { color: "#e7e9ec" },
    identityPreviewEditButton: { color: GOLD },

    formFieldErrorText: { fontSize: "0.85rem", color: "#ff8f8f" },
    formFieldWarningText: { fontSize: "0.85rem", color: "#f0bd55" },
    formFieldSuccessText: { fontSize: "0.85rem", color: "#7fe0a0" },
    alert: {
      background: "rgba(255, 107, 107, 0.1)",
      border: "1px solid rgba(255, 143, 143, 0.35)",
      borderRadius: "10px",
    },
    alertText: { fontSize: "0.85rem", color: "#ffbdbd" },

    dividerLine: { background: "#2b2f35" },
    dividerText: { color: "#9aa0a8" },
    socialButtonsBlockButton: {
      height: "44px",
      color: "#f3f4f5",
      background: "#1a1d21",
      border: `1px solid ${BORDER}`,
      "&:hover": { background: "#22262b", borderColor: BORDER_HOVER },
    },

    footer: { background: "transparent" },
    // Invite-only: no "Sign up" / "Create account" call to action.
    footerAction: { display: "none" },
    footerPages: { color: "#9aa0a8" },
    footerPagesLink: { color: "#9aa0a8" },

    spinner: { color: GOLD },
  },
};

export default clerkAppearance;
